import { ImageLoaderProps } from 'next/image';

import { getAtIndex } from '@lib/arrays/safeArrayAccess';

export type contentfulLoader = {
  customLoaderType: 'contentful';
  origWidth: number;
  origHeight?: number;
  customLoaderFilters?: contentfulImageFilters;
};

export enum ContentfulImageFormatTypes {
  JPG = 'jpg',
  PNG = 'png',
  WEBP = 'webp',
  GIF = 'gif',
  AVIF = 'avif',
}

export enum ContentfulResizeTypes {
  PAD = 'pad',
  FILL = 'fill',
  SCALE = 'scale',
  CROP = 'crop',
  THUMB = 'thumb',
}

export enum ContentfulFocusAreaTypes {
  CENTER = 'center',
  TOP = 'top',
  RIGHT = 'right',
  LEFT = 'left',
  BOTTOM = 'bottom',
  TOP_RIGHT = 'top_right',
  TOP_LEFT = 'top_left',
  BOTTOM_RIGHT = 'bottom_right',
  BOTTOM_LEFT = 'bottom_left',
  FACE = 'face',
  FACES = 'faces',
}

interface contentfulImageFilters {
  resize?: ContentfulResizeTypes;
  focusArea?: ContentfulFocusAreaTypes;
  quality?: number;
  format?: ContentfulImageFormatTypes;
}

const aspectRatioHeight = (width: number, aspectWidth: number, aspectheight = 0): number => {
  return aspectWidth === 0 ? 0 : Math.round((aspectheight / aspectWidth) * width);
};

const getFiltersString = (filters: contentfulImageFilters): string => {
  const { format, resize, focusArea, quality } = filters;

  const formatFilter = format ? `&fm=${format}` : `&fm=${ContentfulImageFormatTypes.AVIF}`;
  const resizeFilter = resize ? `&fit=${resize}` : '';
  const focusAreaFilter = focusArea ? `&f=${focusArea}` : '';
  const qualityFilter = quality && quality > 0 ? `&q=${filters.quality}` : '';

  return `${formatFilter}${resizeFilter}${focusAreaFilter}${qualityFilter}`;
};

export const getContentfulLoader = (
  src: string,
  width: number,
  aspectWidth: number,
  aspectheight = 0,
  filters: contentfulImageFilters = {},
): string => {
  // Contentful URL format: https://images.ctfassets.net/{spaceId}/{assetId}/{uniqueId}/{filename}
  const parts = src.split('/');
  const hostName = getAtIndex(parts, 2);
  const spaceId = getAtIndex(parts, 3);
  const assetId = getAtIndex(parts, 4);
  const uniqueId = getAtIndex(parts, 5);
  const name = getAtIndex(parts, 6);
  const height = aspectRatioHeight(width, aspectWidth, aspectheight);
  const filtersSet = getFiltersString(filters);
  return `https://${hostName}/${spaceId}/${assetId}/${uniqueId}/${name}?w=${width}${height > 0 ? `&h=${height}` : ''}${filtersSet}`;
};

export function createContentfulLoader(
  origWidth: number,
  origHeight = 0,
  filters: contentfulImageFilters = {},
) {
  return ({ src, width, quality }: ImageLoaderProps) =>
    getContentfulLoader(src, width, origWidth, origHeight, { quality, ...filters });
}
